import { cn } from "@/lib/utils";
import {
  formatDateBR,
  formatDateOnly,
  formatDateTime,
  formatRelative,
  formatUtcDate,
} from "@/lib/format";
import { EmptyValue } from "./EmptyValue";

/**
 * Escolha pelo TIPO do dado (ver utils/dateUtils):
 * - date: coluna DATE / "YYYY-MM-DD" (data lógica)
 * - datetime: timestamptz com hora
 * - instant-date: timestamptz exibido só como data
 * - utc-date: data lógica guardada como UTC-meia-noite
 */
export type DateKind = "date" | "datetime" | "instant-date" | "utc-date";

function format(value: string, kind: DateKind, pattern?: string): string {
  switch (kind) {
    case "date":
      return formatDateBR(value, pattern);
    case "datetime":
      return formatDateTime(value, pattern);
    case "instant-date":
      return formatDateOnly(value, pattern);
    case "utc-date":
      return formatUtcDate(value);
  }
}

interface DateTextProps {
  value: string | null | undefined;
  kind: DateKind;
  pattern?: string;
  className?: string;
}

export function DateText({ value, kind, pattern, className }: DateTextProps) {
  if (!value) return <EmptyValue />;
  const full = kind === "datetime" || kind === "instant-date" ? formatDateTime(value) : format(value, kind);
  return (
    <time dateTime={value} title={full} className={cn("tabular-nums", className)}>
      {format(value, kind, pattern)}
    </time>
  );
}

/** "há 2 dias", "em 5 dias", "hoje", com a data exata no hover. */
export function RelativeTime({ value, className }: { value: string | null | undefined; className?: string }) {
  if (!value) return <EmptyValue />;
  const isInstant = value.includes("T");
  return (
    <time
      dateTime={value}
      title={isInstant ? formatDateTime(value) : formatDateBR(value)}
      className={cn("tabular-nums", className)}
    >
      {formatRelative(isInstant ? new Date(value) : value)}
    </time>
  );
}

/** Período de datas lógicas: "14/08 a 20/09/26"; sem fim, "desde 14/08/26". */
export function DateRange({
  start,
  end,
  openEndLabel = "sem data de término",
  className,
}: {
  start: string | null | undefined;
  end: string | null | undefined;
  openEndLabel?: string;
  className?: string;
}) {
  if (!start && !end) return <EmptyValue />;
  if (start && !end)
    return (
      <span className={cn("tabular-nums", className)} title={openEndLabel}>
        desde {formatDateBR(start, "dd/MM/yy")}
      </span>
    );
  if (!start) return <span className={cn("tabular-nums", className)}>até {formatDateBR(end, "dd/MM/yy")}</span>;
  const sameYear = start.slice(0, 4) === end!.slice(0, 4);
  return (
    <span className={cn("tabular-nums", className)}>
      {formatDateBR(start, sameYear ? "dd/MM" : "dd/MM/yy")} a {formatDateBR(end, "dd/MM/yy")}
    </span>
  );
}
